import { useState } from 'react';
import { sendContactForm } from '../../service/ContactService';

interface ContactSectionProps {
    onOpenDemo: () => void;
}

const ContactSection = ({ onOpenDemo }: ContactSectionProps) => {
    const [formData, setFormData] = useState({ name: '', email: '', message: '' });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (error) setError('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.name.trim())    { setError('Name is required');    return; }
        if (!formData.email.trim())   { setError('Email is required');   return; }
        if (!formData.message.trim()) { setError('Message is required'); return; }

        setLoading(true);
        setError('');
        try {
            await sendContactForm(formData.name, formData.email, formData.message);
            setSuccess(true);
            setFormData({ name: '', email: '', message: '' });
        } catch (err) {
            if (err instanceof Error) setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="contact" className="py-20 bg-white">
            <div className="container mx-auto px-4">
                <div className="text-center mb-14">
                    <span className="text-blue-600 font-semibold text-sm uppercase tracking-widest">Get in Touch</span>
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mt-2">Contact Us</h2>
                </div>

                <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-10">

                    {/* Contact form */}
                    <div className="bg-gray-50 p-8 rounded-2xl shadow-sm">
                        <h3 className="text-xl font-bold text-gray-800 mb-6">Send a Message</h3>

                        {success ? (
                            <div className="text-center py-8">
                                <div className="pop-in inline-block mb-4">
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                                        <i className="fas fa-check-circle text-green-500 text-3xl"></i>
                                    </div>
                                </div>
                                <h4 className="text-lg font-semibold text-gray-800 mb-2">Message Sent!</h4>
                                <p className="text-gray-500 text-sm mb-6">Thank you — we'll get back to you within 24 hours.</p>
                                <button
                                    onClick={() => setSuccess(false)}
                                    className="gradient-bg text-white px-6 py-2.5 rounded-xl font-medium hover:opacity-90 transition"
                                >
                                    Send Another Message
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit}>
                                {error && (
                                    <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-xl text-sm">
                                        {error}
                                    </div>
                                )}
                                <div className="mb-5">
                                    <label className="block text-gray-700 font-medium mb-2 text-sm">Name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm transition"
                                        placeholder="Your full name"
                                    />
                                </div>
                                <div className="mb-5">
                                    <label className="block text-gray-700 font-medium mb-2 text-sm">Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm transition"
                                        placeholder="your@email.com"
                                    />
                                </div>
                                <div className="mb-6">
                                    <label className="block text-gray-700 font-medium mb-2 text-sm">Message</label>
                                    <textarea
                                        name="message"
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        rows={4}
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm transition resize-none"
                                        placeholder="How can we help?"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full gradient-bg text-white py-3 rounded-xl font-semibold hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? (
                                        <><i className="fas fa-spinner fa-spin mr-2"></i>Sending…</>
                                    ) : (
                                        <>Send Message <i className="fas fa-paper-plane ml-1"></i></>
                                    )}
                                </button>
                            </form>
                        )}
                    </div>

                    {/* Right column: Book Demo panel + contact info */}
                    <div className="flex flex-col justify-center">
                        <div className="bg-blue-700 rounded-2xl p-8 text-white text-center mb-6">
                            <div className="text-4xl mb-4">
                                <i className="fas fa-calendar-check"></i>
                            </div>
                            <h3 className="text-xl font-bold mb-3">Prefer a Live Demo?</h3>
                            <p className="text-blue-200 text-sm mb-6">
                                Book a free 20-minute call and we'll walk you through the platform, answer your questions, and set up your account on the spot.
                            </p>
                            <button
                                onClick={onOpenDemo}
                                className="w-full cta-amber text-white py-3 rounded-xl font-bold text-base shadow hover:shadow-md transition"
                            >
                                <i className="fas fa-calendar-check mr-2"></i> Book a Free Demo
                            </button>
                        </div>

                        <div className="flex items-center gap-3 text-sm text-gray-600">
                            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                <i className="fas fa-clock text-blue-600"></i>
                            </div>
                            <div>
                                <div className="font-medium text-gray-800">Response Time</div>
                                <div className="text-gray-500">We reply within 24 hours</div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default ContactSection;
